import { DEFAULT_THRESHOLDS } from '../../core/thresholds';
import { formatCardDate, type PeCardView } from '../Reports/programEvaluationCard';
import { programEvaluationReady, type SetupFacts } from './setupStatus';

export type ChecklistStepId = 'sis' | 'access' | 'exitCriteria' | 'years' | 'matching' | 'plans' | 'programEvaluation';
export type ChecklistStepState = 'todo' | 'wait' | 'done';
export type ChecklistStepIcon = ChecklistStepState | 'error';
export type ChecklistAction =
  | 'getStarted'
  | 'upload'
  | 'reviewCriteria'
  | 'reviewYears'
  | 'generatePlans'
  | 'generateEvaluation'
  | 'retryEvaluation';

export type ChecklistStep = {
  id: ChecklistStepId;
  title: string;
  state: ChecklistStepState;
  icon: ChecklistStepIcon;
  subtitle: string;
  description: string;
  action: ChecklistAction | null;
  locked: boolean;
  primary: boolean;
};

export type ChecklistDetails = {
  accessUploadedAt: string | null;
  accessStudentCount: number | null;
  criteria: { stateName: string; source: 'state_record' | 'district' | null } | null;
  years: { newcomerMaxYears: number; ltelMinYears: number } | null;
};

export function stepCount(facts: Pick<SetupFacts, 'sisMatchingRequired'>): number {
  return facts.sisMatchingRequired ? 7 : 6;
}

const STEPS_INTRO = 'These steps unlock your ML students, their action plans and your Program Evaluation.';
const FLASHLIGHT_INTRO = `Flashlight360 is connected, so you can already see benchmarks and assignments. ${STEPS_INTRO}`;

type Draft = Omit<ChecklistStep, 'primary'>;

function dated(label: string, iso: string | null): string {
  const date = iso === null ? '' : formatCardDate(iso);
  return date === '' ? label : `${label} ${date}`;
}

function withDetail(line: string, detail: string | null): string {
  return detail === null ? line : `${line} · ${detail}`;
}

function count(n: number | null, singular: string, plural: string): string | null {
  if (n === null) return null;
  return `${n} ${n === 1 ? singular : plural}`;
}

function yearsLine(years: ChecklistDetails['years']): string {
  const { newcomerMaxYears, ltelMinYears } = years ?? DEFAULT_THRESHOLDS;
  const long = ltelMinYears - 1;
  return `${newcomerMaxYears} ${newcomerMaxYears === 1 ? 'year' : 'years'} and more than ${long} ${long === 1 ? 'year' : 'years'}`;
}

function criteriaLine(criteria: ChecklistDetails['criteria']): string | null {
  if (criteria === null || criteria.source === null) return null;
  return criteria.source === 'district' ? 'Your district’s criteria' : `${criteria.stateName}’s criteria`;
}

function sisStep(facts: SetupFacts): Draft {
  const requested = facts.sisRequestedAt !== null;
  if (facts.sisConnected) {
    return {
      id: 'sis',
      title: 'Connect your SIS',
      state: 'done',
      icon: 'done',
      subtitle: dated('Connected', facts.sisConnectedAt),
      description: 'Your roster now updates on its own.',
      action: null,
      locked: false,
    };
  }
  return {
    id: 'sis',
    title: 'Connect your SIS',
    state: requested ? 'wait' : 'todo',
    icon: requested ? 'wait' : 'todo',
    subtitle: requested ? dated('Requested', facts.sisRequestedAt) : 'Not started',
    description: requested
      ? 'A Flashlight Learning representative will be in touch to connect it.'
      : 'Fill out a short form and a Flashlight Learning representative will help you connect it.',
    action: requested ? null : 'getStarted',
    locked: false,
  };
}

function accessStep(facts: SetupFacts, details: ChecklistDetails): Draft {
  const done = facts.accessUploaded;
  return {
    id: 'access',
    title: 'Upload your ACCESS results',
    state: done ? 'done' : 'todo',
    icon: done ? 'done' : 'todo',
    subtitle: done
      ? withDetail(dated('Uploaded', details.accessUploadedAt), count(details.accessStudentCount, 'student', 'students'))
      : 'Not started',
    description: done
      ? 'Upload again whenever new ACCESS scores come in.'
      : 'WIDA ACCESS scores bring in your ML students and unlock their plans.',
    action: done ? null : 'upload',
    locked: false,
  };
}

function criteriaStep(facts: SetupFacts, details: ChecklistDetails): Draft {
  const confirmed = facts.exitCriteriaConfirmed;
  const line = criteriaLine(details.criteria);
  return {
    id: 'exitCriteria',
    title: 'Confirm your exit criteria',
    state: confirmed ? 'done' : 'todo',
    icon: confirmed ? 'done' : 'todo',
    subtitle: confirmed
      ? withDetail(dated('Confirmed', confirmed.at), line)
      : line === null || details.criteria?.source !== 'state_record'
        ? 'Not started'
        : `Using ${details.criteria.stateName}’s default`,
    description: 'Plans use these to show how close each student is to exiting.',
    action: confirmed ? null : 'reviewCriteria',
    locked: false,
  };
}

function yearsStep(facts: SetupFacts, details: ChecklistDetails): Draft {
  const confirmed = facts.yearsConfirmed;
  return {
    id: 'years',
    title: 'Confirm newcomer and long-term years',
    state: confirmed ? 'done' : 'todo',
    icon: confirmed ? 'done' : 'todo',
    subtitle: confirmed
      ? withDetail(dated('Confirmed', confirmed.at), yearsLine(details.years))
      : `Using defaults: ${yearsLine(details.years)}`,
    description: 'Decides which students count as newcomers or long-term MLs.',
    action: confirmed ? null : 'reviewYears',
    locked: false,
  };
}

function matchingStep(facts: SetupFacts): Draft {
  const base = {
    id: 'matching' as const,
    title: 'Flashlight matches your SIS data',
    description: 'How Flashlight reads your SIS, including who counts as an active, monitored or former EL.',
    action: null,
  };
  if (facts.sisMatchingConfirmedAt !== null) {
    return { ...base, state: 'done', icon: 'done', subtitle: dated('Matched', facts.sisMatchingConfirmedAt), locked: false };
  }
  if (!facts.sisConnected) {
    return { ...base, state: 'todo', icon: 'todo', subtitle: 'Needs your SIS', locked: true };
  }
  return { ...base, state: 'wait', icon: 'wait', subtitle: 'Waiting on Flashlight', locked: false };
}

export function plansReady(facts: SetupFacts): boolean {
  return facts.accessUploaded && facts.exitCriteriaConfirmed !== null && facts.yearsConfirmed !== null;
}

function plansStep(facts: SetupFacts, details: ChecklistDetails): Draft {
  const done = facts.plansGenerated;
  const ready = plansReady(facts);
  return {
    id: 'plans',
    title: 'Generate Student Action Plans',
    state: done ? 'done' : 'todo',
    icon: done ? 'done' : 'todo',
    subtitle: done
      ? withDetail(dated('Generated', facts.plansGeneratedAt), count(details.accessStudentCount, 'plan', 'plans'))
      : ready
        ? 'Ready to generate'
        : 'Needs ACCESS results and confirmed settings',
    description: done
      ? 'Open any student on the Students page to see their plan.'
      : 'Creates a plan for every student with ACCESS results.',
    action: done ? null : 'generatePlans',
    locked: !done && !ready,
  };
}

function lockedEvaluationLine(facts: SetupFacts): string {
  if (!facts.accessUploaded && !facts.sisConnected) return 'Needs ACCESS results and your SIS';
  if (!facts.sisConnected) return 'Needs your SIS';
  if (!facts.accessUploaded) return 'Needs ACCESS results';
  return 'Needs Flashlight to match your SIS data';
}

function evaluationStep(facts: SetupFacts, evaluation: PeCardView): Draft {
  const base = {
    id: 'programEvaluation' as const,
    title: 'Generate your Program Evaluation',
    description: 'A district-wide view of proficiency, growth and exits.',
  };
  if (!programEvaluationReady(facts) || evaluation.kind === 'preview' || evaluation.kind === 'unavailable') {
    return { ...base, state: 'todo', icon: 'todo', subtitle: lockedEvaluationLine(facts), action: 'generateEvaluation', locked: true };
  }
  switch (evaluation.kind) {
    case 'generated':
    case 'stale':
      return { ...base, state: 'done', icon: 'done', subtitle: dated('Generated', evaluation.generatedAt), action: null, locked: false };
    case 'failed':
      return { ...base, state: 'todo', icon: 'error', subtitle: 'Couldn’t generate', action: 'retryEvaluation', locked: false };
    case 'generate':
      return { ...base, state: 'todo', icon: 'todo', subtitle: 'Ready to generate', action: 'generateEvaluation', locked: false };
    default:
      return { ...base, state: 'wait', icon: 'wait', subtitle: 'In progress', action: null, locked: false };
  }
}

export function checklistSteps(facts: SetupFacts, details: ChecklistDetails, evaluation: PeCardView): ChecklistStep[] {
  const drafts = [
    sisStep(facts),
    accessStep(facts, details),
    criteriaStep(facts, details),
    yearsStep(facts, details),
    ...(facts.sisMatchingRequired ? [matchingStep(facts)] : []),
    plansStep(facts, details),
    evaluationStep(facts, evaluation),
  ];
  let primaryUsed = false;
  return drafts.map((draft) => {
    const primary = !primaryUsed && draft.action !== null && draft.state === 'todo' && !draft.locked;
    if (primary) primaryUsed = true;
    return { ...draft, primary };
  });
}

export function checklistNext(steps: ChecklistStep[]): ChecklistStep {
  const next = steps.find((s) => s.primary) ?? steps.find((s) => s.state !== 'done');
  return next ?? steps[steps.length - 1]!;
}

export function checklistDoneCount(steps: ChecklistStep[]): number {
  return steps.filter((s) => s.state === 'done').length;
}

export function checklistIntro(facts: SetupFacts, evaluation: PeCardView, flashlightConnected: boolean | null): string {
  if (facts.sisRequestedAt !== null && !facts.sisConnected && !facts.accessUploaded) {
    return 'Thanks, we’ve got your SIS request. While a Flashlight Learning representative connects it, upload your ACCESS results to see your ML students and their plans.';
  }
  if (facts.accessUploaded && !facts.plansGenerated) {
    return 'Your ACCESS results are in and your ML students are on the Students page. Generate their action plans next.';
  }
  if (facts.plansGenerated && facts.accessUploaded && facts.sisConnected && facts.sisMatchingRequired && facts.sisMatchingConfirmedAt === null) {
    return 'Flashlight is matching your SIS data. Your Program Evaluation unlocks when it’s done.';
  }
  if (facts.plansGenerated && programEvaluationReady(facts) && evaluation.kind === 'generate') {
    return 'Your SIS is connected. Generate your Program Evaluation to finish setup.';
  }
  return flashlightConnected === true ? FLASHLIGHT_INTRO : STEPS_INTRO;
}
